/**
 * Marketing-facing copy for /marketing-assets — written for USANA Marketing team.
 * Screens + feature descriptions + ready-to-use campaign copy.
 */

import {
    MARKETING_ADDITIONAL_PROGRAMS,
    MARKETING_ADDITIONAL_PROGRAMS_DISCLAIMER,
} from './marketingAdditionalProgramsCatalog';

export const MARKETING_PAGE = {
    eyebrow: 'Product Preview Guide',
    title: 'Oola Framework',
    subtitle:
        'A walkthrough of the complete member experience — what’s included, how it works, and suggested copy for shop pages, emails, and campaigns.',
    demoNote:
        'All screens below use a fictional demo member, Jordan Rivera (Member Level 1). No real customer data is shown.',
    interfaceNote:
        'This preview reflects the classic Oola Framework interface (V1) — the experience members receive with their Framework purchase.',
};

export const MARKETING_PHASES = [
    {
        id: 'phase1',
        label: 'Phase 1',
        name: 'Design My Life Blueprint',
        timing: 'Onboarding',
        summary: '70-question assessment, 21 goals → Top 7 + Oola One, daily action steps.',
        sections: ['step1', 'step2', 'step3'],
    },
    {
        id: 'phase2',
        label: 'Phase 2',
        name: 'My Breakthrough Experience',
        timing: '12 weeks',
        summary: '12 guided modules — Watch, Read, Do. One new module unlocks every 7 days.',
        sections: ['elearning', 'whats-inside'],
    },
    {
        id: 'daily',
        label: 'Every Day',
        name: 'Member Dashboard',
        timing: 'Ongoing',
        summary: 'Action Tracker, Goals, streaks, badges, and E-Learning — all in one home.',
        sections: ['dashboard', 'tracker-ring', 'tracker-goals'],
    },
    {
        id: 'phase3',
        label: 'Phase 3',
        name: 'My Path',
        timing: '9 months',
        summary: 'Personalized journey after Breakthrough — one new module every 30 days.',
        sections: ['my-path'],
    },
    {
        id: 'addons',
        label: 'Optional',
        name: 'Additional Programs',
        timing: 'Add-on purchase',
        summary: 'Standalone courses (Green Gap, Miracle Mindset, 23 Strong, etc.) sold apart from the Framework — confirm availability and pricing with Dave.',
        sections: ['additional-programs'],
    },
];

export const MARKETING_HOW_TO_USE = {
    title: 'How to use this guide',
    items: [
        'Scroll through each section to see the actual member interface and understand what the Framework delivers.',
        'Use the headline and “Suggested copy” blocks directly in shop pages, product emails, and campaign creative.',
        'The “Recommended for” notes indicate which marketing assets each screen is best suited for (shop hero, email header, social, etc.).',
        'Screens are static previews for reference. An interactive demo can be provided separately if needed.',
    ],
};

export const MARKETING_FRAMEWORK_OVERVIEW = {
    title: 'What members receive',
    summary:
        'The Oola Framework is a structured life-transformation program — not a single course, but a complete journey with assessment, goal-setting, daily accountability, and guided learning across every area of life.',
    bullets: [
        'Life assessment across 7 key areas (Fitness, Finance, Family, Faith, Field, Friends, Fun) — 70 questions with a visual Oola Wheel score.',
        'Goal-setting system: 21 goals narrowed to a Top 7, plus one Oola One priority goal for the year.',
        'Daily action steps — small habits members check off every day to build streaks and earn badges.',
        '12-week Breakthrough Experience with weekly video lessons, reading, and workbook exercises.',
        '9-month My Path journey after Breakthrough — ongoing personalized modules.',
        'Optional add-on programs for members who want to go deeper.',
    ],
};

export const MARKETING_SECTION_NAV = [
    { id: 'step1', label: 'Step 1 — Assessment' },
    { id: 'step2', label: 'Step 2 — Goals' },
    { id: 'step3', label: 'Step 3 — Daily Actions' },
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'whats-inside', label: 'What\'s Inside' },
    { id: 'elearning', label: 'E-Learning Lesson' },
    { id: 'tracker-ring', label: 'Tracker — Streak Ring' },
    { id: 'tracker-goals', label: 'Daily Actions & Goals' },
    { id: 'desktop-mobile', label: 'Desktop + Mobile' },
    { id: 'mobile-only', label: 'Mobile' },
    { id: 'email', label: 'Email Composite' },
    { id: 'my-path', label: 'My Path' },
    { id: 'additional-programs', label: 'Additional Programs' },
];

/** Per-section copy shown above each product preview. */
export const MARKETING_SECTION_COPY = {
    step1: {
        title: 'Step 1 — Design My Life Blueprint',
        phase: 'Phase 1 · Onboarding',
        angle: 'Know where you are today.',
        summary:
            'Every member journey begins with an honest look at life balance. Step 1 is a guided assessment — not a test, but a starting point that shows members exactly where they stand across all seven areas of the Oola life wheel.',
        includes: [
            'Welcome video: “Step 1: Where You Are Now.”',
            '70 questions total — 10 per life area (Fitness, Finance, Family, Faith, Field, Friends, Fun).',
            'Simple 1–10 rating scale; members can save and return anytime.',
            'Live Oola Wheel that fills in as they progress through each area.',
            'Completion: personal Oola Score + full wheel visualization.',
            'Score is retested at month 6 and month 12 to measure growth over time.',
            'Celebration moment with option to share; then members advance to Step 2.',
        ],
        shopCopy:
            'Start with a 70-question life assessment across 7 key areas. See your Oola Wheel and baseline score — your starting point for building a balanced life.',
        recommendedFor: 'Onboarding explainer, “what happens first” shop section, email welcome series.',
    },
    step2: {
        title: 'Step 2 — Where You Want to Go',
        phase: 'Phase 1 · Onboarding',
        angle: 'Choose where you want to go.',
        summary:
            'Once members know where they are, Step 2 helps them define where they want to be. They build a full list of goals, narrow to their Top 7, and choose one Oola One — the single goal that matters most for the next 365 days.',
        includes: [
            'Build 21 goals — 3 per life area, each with a target date (from recommendations or custom).',
            'Oola Wheel stays visible with assessment scores for context.',
            'Pick 7 — select the goals that will bring the most balance (one per area).',
            'Drag-and-drop to rank goals in priority order.',
            'Choose Oola One — the #1 goal for the next year.',
            'Celebration + share goals → advance to Step 3.',
        ],
        shopCopy:
            'Define 21 goals across 7 life areas, narrow to your Top 7, rank them, and choose your Oola One — the one goal that matters most this year.',
        recommendedFor: 'Goal-setting feature highlight, social carousel, “clarity before action” messaging.',
    },
    step3: {
        title: 'Step 3 — How You Get There',
        phase: 'Phase 1 · Onboarding',
        angle: 'Daily steps, not someday plans.',
        summary:
            'Goals only matter if members act on them daily. Step 3 bridges intention and habit — members define small, concrete actions for the next 24 hours, tied to their Top 7 goals.',
        includes: [
            'Establish 3 or more daily action steps.',
            'Top 7 goals displayed alongside for context (with target dates).',
            'Flexible: 3 minimum to continue, up to 15 daily actions.',
            'Real examples: “Walk 20 minutes after lunch,” “Spend 15 minutes on my budget.”',
            'Completion → members land on the Dashboard to start tracking.',
        ],
        shopCopy:
            'Turn goals into daily habits. Define 3 or more small action steps every day — the daily rhythm that drives real change.',
        recommendedFor: 'Habit/accountability messaging, “how it works daily” shop section.',
    },
    dashboard: {
        title: 'Dashboard — Daily Home',
        phase: 'Every Day',
        angle: 'Everything in one place, every day.',
        summary:
            'After onboarding, the Dashboard becomes the member’s daily command center. Progress stats, action tracking, goals, and learning — all accessible without navigating away.',
        includes: [
            'My Progress bar: Action Steps Taken, Current Streak, Longest Streak, Goals Completed.',
            'Action Tracker with 3 views: daily ritual ring, daily actions checklist, and to-do list.',
            'Complete 3 daily actions to earn the day’s streak; badges at 3, 7, 30, 77, 100, 365+ days.',
            'My Goals: Top 7 (Oola One highlighted), Achieved, and Additional goals.',
            'E-Learning access: Breakthrough modules, My Path, and Additional Programs.',
            'Achievements: Badges, Milestones, and Streaks (First Steps, Goal Setter, Goal Crushed, and more).',
        ],
        shopCopy:
            'Your daily home base: check off action steps, track Top 7 goals, build streaks, earn badges, and access Breakthrough lessons — all in one place.',
        recommendedFor: 'Shop hero banner (1920×720), primary product image, “what you get daily” section.',
    },
    whatsInside: {
        title: 'What\'s Inside — Full Value View',
        phase: 'Campaign Composite',
        angle: 'See the full Framework at a glance.',
        summary:
            'A side-by-side view built for marketing: daily accountability (profile, progress, tracker, goals) on the left, a Breakthrough lesson with video on the right. Shows members get both habit tracking and guided learning in one experience.',
        includes: [
            'Left: member profile, progress stats, Action Tracker, and My Goals.',
            'Right: Breakthrough module — title, lesson copy, and video player.',
            'Single header — designed as a shop/email hero, not a separate member screen.',
            'For a full-width dashboard shot, use the Dashboard section instead.',
        ],
        shopCopy:
            'Daily accountability meets guided learning. Track your actions and goals while working through weekly Breakthrough modules — Watch, Read, and Do.',
        recommendedFor: '“What’s in the box” shop banner, landing page hero, campaign overview slide.',
    },
    elearning: {
        title: 'E-Learning — Breakthrough Lesson',
        phase: 'Phase 2 · 12 Weeks',
        angle: 'Guided learning, not just content.',
        summary:
            'Every Breakthrough module follows the same proven three-part format. Members watch a video lesson, read supporting content, then complete workbook exercises — learning by doing, not just watching.',
        includes: [
            'Watch — video lesson teaches the core concept.',
            'Read — written content and imagery reinforce the lesson.',
            'Do — workbook exercises to apply what they learned.',
            '12 modules total — one unlocks every 7 days over 12 weeks.',
            'Covers all 7 life areas plus commitment, action, community, and clarity.',
            'Personalized program created from a short member assessment.',
        ],
        shopCopy:
            'Each Breakthrough module is Watch → Read → Do. Video teaches, workbook applies — 12 weekly modules covering every area of your Oola life.',
        recommendedFor: 'Education/curriculum section, “learn by doing” messaging, module preview.',
    },
    trackerRing: {
        title: 'Action Tracker — Streak Ring',
        phase: 'Every Day',
        angle: 'Build your streak, one day at a time.',
        summary:
            'The Tracker tab is the daily ritual view — a visual streak ring showing progress toward today\'s 3 actions, plus lifetime action steps. This is the gamification hook that keeps members coming back.',
        includes: [
            'Tracker tab: circular streak ring with today\'s progress (e.g. 2 of 3 actions).',
            'Complete 3 daily actions to earn the day\'s streak.',
            'Lifetime action steps counter below the ring.',
            'Shown alongside My Goals so members see habits and priorities together.',
        ],
        shopCopy:
            'Complete 3 daily actions and earn your streak. Track lifetime progress with a visual daily ritual ring that keeps you accountable.',
        recommendedFor: 'Streak/gamification messaging, social ads, “build the habit” shop section.',
    },
    trackerGoals: {
        title: 'Daily Actions & Goals',
        phase: 'Every Day',
        angle: 'Accountability built in.',
        summary:
            'The Daily Actions tab is where members check off today\'s steps. Shown next to My Goals so the connection between daily habits and long-term priorities is clear.',
        includes: [
            'Daily Actions tab: numbered checklist for today\'s action steps.',
            'Check off each step — 3 completed = streak earned.',
            'My Goals: Top 7 with Oola One highlighted, plus Achieved and Additional tabs.',
            'Members can mark goals complete and promote additional goals to Top 7.',
        ],
        shopCopy:
            'Check off daily action steps, build streaks, earn badges — and track your Top 7 goals including your Oola One. Accountability that keeps you moving.',
        recommendedFor: 'Accountability/streak feature highlight, social proof graphics, email modules.',
    },
    desktopMobile: {
        title: 'Desktop + Mobile',
        phase: 'Every Day',
        angle: 'Your Framework, anywhere.',
        summary:
            'Members access the same full experience on desktop and mobile — responsive design so they can check off actions, review goals, and watch lessons from any device.',
        includes: [
            'Full dashboard, tracker, goals, and e-learning on desktop.',
            'Mobile-optimized layout — not a scaled-down version, but a true mobile experience.',
            'Ideal for device mockups in shop, social, and lifestyle creative.',
        ],
        shopCopy:
            'Access your Oola Framework from any device — desktop or mobile. Track actions, goals, and lessons wherever you are.',
        recommendedFor: 'Device mockups, lifestyle photography overlays, “access anywhere” messaging.',
    },
    mobileOnly: {
        title: 'Mobile Dashboard',
        phase: 'Every Day',
        angle: 'Life balance in your pocket.',
        summary:
            'The mobile dashboard gives members their daily tools on the go — progress, actions, goals, and lessons optimized for phone screens.',
        includes: [
            'Full mobile dashboard with progress stats, tracker, and e-learning.',
            'Ready for phone-only social ads and mobile shop banners.',
        ],
        shopCopy:
            'Take your Oola journey everywhere. Daily actions, goals, and lessons — optimized for mobile.',
        recommendedFor: 'Instagram Stories, mobile shop banners, app-style social creative.',
    },
    email: {
        title: 'Email & Campaign Composite',
        phase: 'Campaign Composite',
        angle: 'One frame, full story.',
        summary:
            'The real member dashboard home — same layout members see every day — with a campaign headline on top. Production Tracker, Goals, and E-Learning board with demo data.',
        includes: [
            'Headline: “Inside the Oola Framework.”',
            'Full V1 dashboard: profile, My Progress, Action Tracker (Tracker tab), My Goals, Oola Score, Breakthrough modules.',
            'Production UI components — not a custom composite grid.',
            'Export at 1920 × 720 (shop/email hero). For lesson detail, use the E-Learning section.',
        ],
        shopCopy:
            'Inside the Oola Framework: daily action tracking, goal progress, and guided Breakthrough lessons — everything members need to transform their life.',
        recommendedFor: 'Email header, social square post, campaign landing page visual.',
    },
    myPath: {
        title: 'My Path — Phase 3',
        phase: 'Phase 3 · 9 Months',
        angle: 'Your deeper journey is waiting.',
        summary:
            'After completing the 12-week Breakthrough, members unlock My Path — a personalized 9-month journey with new modules every 30 days to keep momentum going.',
        includes: [
            'Left: My Path module library (2 completed, 1 in progress, 1 locked).',
            'Right: “Fuel your Passion for Fitness” open in Watch mode (video + lesson copy).',
            'While locked on dashboard, members see Breakthrough progress with a clear “continue” prompt.',
        ],
        shopCopy:
            'After Breakthrough, unlock My Path — a personalized 9-month journey with new modules every 30 days to keep your transformation going.',
        recommendedFor: 'Long-term value messaging, retention/renewal campaigns, “what comes next” section.',
    },
    additionalPrograms: {
        title: 'Additional Programs',
        phase: 'Optional Add-Ons',
        angle: 'Go deeper.',
        summary:
            'Standalone courses sold separately from the core Oola Framework. Once purchased, they appear in the E-Learning hub under Additional Programs — same Watch / Read / Do format as Breakthrough, each with its own module library.',
        includes: [
            'Preview below: portrait covers 2×2 (shop cards), then wide covers for Who Are You and Coach Certification.',
            'Separate tab in MY E-LEARNING: Additional Programs after purchase.',
            'Same Watch / Read / Do format as Breakthrough; each course has its own modules.',
            'Lifetime access to purchased programs — even for limited (non-subscriber) members.',
        ],
        disclaimer: MARKETING_ADDITIONAL_PROGRAMS_DISCLAIMER,
        programs: MARKETING_ADDITIONAL_PROGRAMS,
        shopCopy:
            'Go deeper beyond the Framework with optional programs — standalone courses with their own guided modules, available inside your E-Learning hub after purchase.',
        recommendedFor: 'Upsell/cross-sell section, bundle messaging, premium tier creative — pending Dave confirmation on availability and pricing.',
    },
};

/** Internal-only — shown in collapsed “Asset export” section, not for marketing audience. */
export const MARKETING_EXPORT_NOTE = {
    title: 'Asset export (internal)',
    body: 'Add ?capture=1 to hide this guide and show screens only. Add &section=dashboard to isolate one screen. Suggested dimensions are listed per section for PNG export.',
};

export const MARKETING_SECTION_SIZES = {
    step1: '1600 × 900',
    step2: '1600 × 918 (cropped at Finance goal 2)',
    step3: '1600 × 900',
    dashboard: '1920 × 720 (shop hero) or 1600 × 900',
    'whats-inside': '1600 × 900',
    elearning: '1600 × 900',
    'tracker-ring': '1600 × 900',
    'tracker-goals': '1600 × 900 or 1200 × 1200',
    'desktop-mobile': '1600 × 900',
    'mobile-only': '390 × 844',
    email: '1920 × 720 (shop hero)',
    'my-path': '1600 × 900',
    'additional-programs': '1600 × 900',
};
